import { Battery, Sun, Zap } from "lucide-react";
import type { SystemSummaryItem, SystemSummaryItemKey } from "@/api/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function placeholderIcon(key: SystemSummaryItemKey) {
  if (key === "battery") return Battery;
  if (key === "inverter") return Zap;
  return Sun;
}

export default function ComponentLightbox({
  item,
  open,
  onOpenChange,
}: {
  item: SystemSummaryItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const title = item?.name?.trim() || item?.label || "Component";
  const Icon = item ? placeholderIcon(item.key) : Sun;
  const imageUrl = item?.imageUrl?.trim() || "";
  const manufacturer = item?.manufacturer?.trim() || "";
  const description = item?.description?.trim() || "";
  const specs = item?.specs?.filter((spec) => spec.label && spec.value) ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden border-white/10 bg-[#0d1b2e] p-0 text-white sm:max-w-lg [&_[data-slot=dialog-close]]:text-[#D4FF00] [&_[data-slot=dialog-close]]:opacity-80 [&_[data-slot=dialog-close]]:hover:opacity-100">
        <DialogHeader className="px-6 pt-6 pr-12">
          <DialogTitle className="font-display text-xl text-white">{title}</DialogTitle>
          {manufacturer ? (
            <DialogDescription className="text-white/50">
              {manufacturer}
            </DialogDescription>
          ) : (
            <DialogDescription className="sr-only">
              {item?.label ?? "System component"}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-4 px-6 pb-6 pt-4">
          <div className="flex aspect-[4/3] items-center justify-center rounded-xl border border-white/10 bg-[#050d1a] p-6">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={title}
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <Icon className="h-16 w-16 text-[#D4FF00]/70" aria-hidden />
            )}
          </div>
          {specs.length > 0 && (
            <dl className="space-y-2">
              {specs.map((spec) => (
                <div
                  key={`${spec.label}-${spec.value}`}
                  className="flex justify-between gap-4 text-sm"
                >
                  <dt className="text-white/50">{spec.label}</dt>
                  <dd className="font-medium text-white">{spec.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {description && (
            <p className="text-sm leading-relaxed text-white/60">{description}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
